import { FieldProps, getIn } from 'formik';
import { DateTimePickerProps } from './DateTimePicker/DateTimePicker';
import { DateTimePicker as BaseDateTimePicker } from './DateTimePicker';
import BasePhoneNumber, { PhoneNumberProps } from './PhoneNumber';
import BaseSelect, { AutocompleteSelect as BaseAutoCompleteSelect } from './Select';
import BaseInput, { InputProps as BaseInputProps } from './Input';
import BaseSwitch, { SwitchProps } from './Switch';
import BaseCheckbox, { CheckboxProps } from './Checkbox';
import BasePillSelect, { PillSelectProps } from './PillSelect';
import BaseTextArea, { TextAreaProps } from './TextArea';
import BaseNoteTitleInput, { NoteTitleInputProps } from './NoteTitleInput';
import BaseNoteContentInput, { NoteContentInputProps } from './NoteContentInput';
import BaseTempImagePicker, {
    TempImagePickerProps,
    TempAndExistingImagePickerProps,
    TempAndExistingImagePicker as BaseTempAndExistingImagePicker,
} from '../ImagePicker/TempImagePicker';
import PermissionsComponent, { PermissionsInputOwnProps } from '../../shared/PermissionsInput';
import BaseColorPickerInput from './ColorPickerInput';
import { ABAC } from '../../../primary-record-types';
import { AutocompleteSelectProps, SelectProps } from './Select/types';
import BaseLargeTextInput, { LargeTextInputInputProps } from './LargeTextInput';

type BaseComponentProps = {
    onChange: (value: any) => void;
    value: any;
    error?: string;
    onBlur: () => void;
    testID?: string;
};

type InputProps = {
    testID?: string;
};

type CustomFieldProps = {
    dependentOnChange?: (value: any) => void;
};

export type FormikFieldProps<TProps extends InputProps> = TProps & FieldProps & CustomFieldProps;

const getFormikInputProps = <TProps extends InputProps>(
    props: FormikFieldProps<TProps>,
    mapOnChange?: (value: any) => any,
): TProps & BaseComponentProps => {
    const {
        field: { name, value },
        form: { handleBlur, setFieldValue, setFieldTouched },
        dependentOnChange,
        ...toPass
    } = props;

    const validationError = getIn(props.form.errors, name);
    const touched = getIn(props.form.touched, name);
    const error = touched && validationError ? validationError : null;

    return {
        ...toPass,
        testID: props.testID || name,
        value,
        error,
        onBlur: () => {
            setFieldTouched(name);
            handleBlur(name);
        },
        onChange: (value: any) => {
            setFieldValue(name, mapOnChange ? mapOnChange(value) : value);
            dependentOnChange && dependentOnChange(value);
        },
    } as any as TProps & BaseComponentProps;
};

export const DateTimePicker: React.FC<FormikFieldProps<DateTimePickerProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseDateTimePicker {...baseComponentProps} />;
};

export const PhoneNumber: React.FC<FormikFieldProps<PhoneNumberProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BasePhoneNumber {...baseComponentProps} />;
};

export const Select: React.FC<FormikFieldProps<SelectProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseSelect {...baseComponentProps} />;
};

export const Input: React.FC<FormikFieldProps<BaseInputProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props, (e) => e.nativeEvent.text);

    return <BaseInput {...baseComponentProps} />;
};

export const Switch: React.FC<FormikFieldProps<SwitchProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseSwitch {...baseComponentProps} />;
};

export const Checkbox: React.FC<FormikFieldProps<CheckboxProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseCheckbox {...baseComponentProps} />;
};

export const PillSelect: React.FC<FormikFieldProps<PillSelectProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BasePillSelect {...baseComponentProps} />;
};

export const TextArea: React.FC<FormikFieldProps<TextAreaProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props, (e) => e.nativeEvent.text);

    return <BaseTextArea {...baseComponentProps} />;
};

export const TempImagePicker: React.FC<FormikFieldProps<TempImagePickerProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseTempImagePicker {...baseComponentProps} />;
};

export const TempAndExistingImagePicker: React.FC<FormikFieldProps<TempAndExistingImagePickerProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseTempAndExistingImagePicker {...baseComponentProps} />;
};

export const NoteTitleInput: React.FC<FormikFieldProps<NoteTitleInputProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props, (e) => e.nativeEvent.text);

    return <BaseNoteTitleInput {...baseComponentProps} />;
};

export const NoteContentInput: React.FC<FormikFieldProps<NoteContentInputProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props, (e) => e);

    return <BaseNoteContentInput {...baseComponentProps} />;
};

export const LargeTextInput: React.FC<FormikFieldProps<LargeTextInputInputProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props, (e) => e.nativeEvent.text);

    return <BaseLargeTextInput {...baseComponentProps} />;
};

export const AutocompleteSelect: React.FC<FormikFieldProps<AutocompleteSelectProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseAutoCompleteSelect {...baseComponentProps} />;
};

export const PermissionsInput: React.FC<PermissionsInputOwnProps & FieldProps<ABAC.AppPermissions>> = (props) => {
    const {
        field: { name, value },
        form: { setFieldValue },
        ...toPass
    } = props;

    return (
        <PermissionsComponent
            {...toPass}
            value={value}
            onChange={(date) => {
                setFieldValue(name, date);
            }}
        />
    );
};

export const ColorPickerInput: React.FC<FormikFieldProps<InputProps>> = (props) => {
    const baseComponentProps = getFormikInputProps(props);

    return <BaseColorPickerInput {...baseComponentProps} />;
};
